import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Play } from "lucide-react";
import { useScrollReveal } from "@/hooks/useScrollReveal";

const GlobalSearchDemo: React.FC = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const { ref: titleRef, isVisible: titleVisible } = useScrollReveal();
  const { ref: contentRef, isVisible: contentVisible } = useScrollReveal({ threshold: 0.2 });

  const handleIframeLoad = () => {
    setIsLoading(false);
  };

  return (
    <section className="py-16 md:py-24 bg-gradient-to-b from-white to-gray-50/50 relative overflow-hidden">
      {/* Subtle background elements */}
      <div className="absolute inset-0 z-0 opacity-10">
        <div className="absolute top-20 right-20 w-72 h-72 rounded-full bg-[#4ba3c7] blur-3xl"></div>
        <div className="absolute bottom-20 left-20 w-96 h-96 rounded-full bg-[#FA8C00] blur-3xl"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 relative z-10">
        {/* Header Section */}
        <div 
          ref={titleRef}
          className={`text-center mb-12 transition-all duration-1000 ${
            titleVisible 
              ? 'opacity-100 translate-y-0' 
              : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="inline-block bg-gradient-to-r from-blue-600 to-purple-600 text-white text-sm font-semibold px-6 py-2 rounded-full mb-6">
            See It In Action
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800 mb-4">
            Global Search - Find <span className="text-[#FA8C00]">Anything</span> Instantly
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
            The only youth program software where you can find any student, class, or family in one search. 
            No more clicking through endless menus or hunting through spreadsheets.
          </p>
        </div>

        {/* Demo Container */}
        <div 
          ref={contentRef}
          className={`transition-all duration-1000 delay-300 ${
            contentVisible 
              ? 'opacity-100 translate-y-0' 
              : 'opacity-0 translate-y-12'
          }`}
        >
          <div className="max-w-5xl mx-auto">
            {/* Demo Frame */}
            <div className="relative bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden">
              {/* Demo Header */}
              <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 border-b border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="flex gap-2">
                    <div className="w-3 h-3 bg-red-400 rounded-full"></div>
                    <div className="w-3 h-3 bg-yellow-400 rounded-full"></div>
                    <div className="w-3 h-3 bg-green-400 rounded-full"></div>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Play className="h-4 w-4 text-[#FA8C00]" />
                    <span className="font-medium">Global Search Demo</span>
                  </div>
                </div>
              </div>

              {/* Embed Container */}
              <div className="relative">
                {/* Loading Skeleton */}
                {isLoading && (
                  <div className="absolute inset-0 z-10 bg-gray-50 flex items-center justify-center">
                    <div className="animate-pulse flex flex-col items-center gap-4">
                      <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center">
                        <Search className="h-8 w-8 text-gray-400" />
                      </div>
                      <div className="text-gray-500 font-medium">Loading Demo...</div>
                      <div className="flex gap-2">
                        <div className="w-2 h-2 bg-[#FA8C00] rounded-full animate-bounce"></div>
                        <div className="w-2 h-2 bg-[#FA8C00] rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                        <div className="w-2 h-2 bg-[#FA8C00] rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Enhanced Embed */}
                <div className="relative w-full" style={{ paddingBottom: '55.24%' }}>
                  <iframe 
                    src="https://app.usehexus.com/embed/64eaeb4b-442d-4c81-bae2-50c8ba46446f?refid=" 
                    frameBorder="0" 
                    loading="lazy" 
                    width="100%" 
                    height="100%" 
                    allowFullScreen 
                    style={{ 
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%'
                    }}
                    title="Hivetivity Global Search Demo - See how to find any student, class, or family instantly"
                    onLoad={handleIframeLoad}
                    className="rounded-b-2xl"
                  />
                </div>
              </div>
            </div>

            {/* Call-to-Action Below Demo */}
            <div className="text-center mt-8">
              <p className="text-gray-600 mb-4 text-base">
                Ready to experience the power of global search in your youth program?
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button 
                  onClick={() => window.location.href = 'https://www.hivetivity.com/business-trial'}
                  className="inline-flex items-center gap-2 bg-[#FA8C00] hover:bg-[#e67c00] text-white font-medium px-6 py-3 rounded-lg transition-all duration-300 shadow-md hover:shadow-lg text-base"
                >
                  <Search className="h-5 w-5" />
                  Start Your Free Trial
                </button>
                <button 
                  onClick={() => window.location.href = 'https://www.hivetivity.com/contact'}
                  className="inline-flex items-center gap-2 bg-white hover:bg-gray-50 text-gray-800 font-medium px-6 py-3 rounded-lg border-2 border-gray-200 hover:border-[#FA8C00] transition-all duration-300 text-base"
                >
                  <Play className="h-5 w-5" />
                  Schedule a Demo
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default GlobalSearchDemo;